// @flow

import * as React from "react";

import { useHistory } from "react-router-dom";
import LinkHandler from "../atoms/LinkHandler";
import Image from "../atoms/Image";
import Pill from "../atoms/Pill";
import Icon from "../atoms/Icon";
import TextButton from "../atoms/TextButton";
import Button from "../atoms/Button";
import { maybePluralize } from "../../utils/stringHandler";
import type { Function } from "ast-types-flow";
import { ReactComponent as Circle } from "../../../assets/images/shape-circle.svg";
import GTM from "../../utils/gtm";
import isExternalUrl from "../../utils/isExternalUrl";
import processInternalUrl from "../../utils/processInternalUrl";

// import { type LinkType } from "../../types/";

type CardVariationsType =
    | "event"
    | "club"
    | "page"
    | "product"
    | "ad"
    | "club_membership"
    | "ticket"
    | "club_event";

type PropsType = {
    url?: string,
    link?: any,
    target?: string,
    featured?: boolean,
    title: string,
    description?: string,
    badge?: string,
    tags?: Array<any>,
    eyebrow?: string,
    image?: any,
    logo?: string,
    variation: CardVariationsType,
    expanded?: boolean,
    hero?: boolean,
    size?: "lg" | "md" | "sm",
    joined_date?: string,
    expiry_date?: string,
    isPastEvent?: boolean,
    ticket_count?: number,
    onSale?: boolean,
    onClick?: Function,
    sold_out?: boolean,
    alert?: string,
    status: string,
    noDescription: boolean,
    cancelled: boolean,
    manualAttendanceAllowed?: boolean,
};

const EventStateBadge = ({ sold_out, cancelled }): React.Node => {
    if (cancelled) {
        return (
            <div className="tile__sold-out">
                <Pill color="error">Cancelled</Pill>
            </div>
        );
    } else if (sold_out) {
        return (
            <div className="tile__sold-out">
                <Pill color="outline">Sold out</Pill>
            </div>
        );
    }

    return null;
};

const Tile = (props: PropsType): React.Node => {
    const GREEN_PILL_LIST = ["event", "ticket", "club_event"];
    const status = [
        {
            label: "Published",
            value: "publish",
            class: "publish",
        },
        {
            label: "Published",
            value: "no-revision",
            class: "publish",
        },
        {
            label: "Draft",
            value: "draft",
            class: "draft",
        },
        {
            label: "Draft",
            value: "in-review",
            class: "draft",
        },
    ].filter(status => status.value === props.status)[0];

    const history = useHistory();
    const tileUrl = !props.hero
        ? processInternalUrl(
              props.link && props.link.url ? props.link.url : props.url
          )
        : "";
    const RootEl = tileUrl ? LinkHandler : "div";
    const classes = ["tile"];
    let tagIcon = null;
    let tagList = null;

    const handleTileClick = e => {
        e.preventDefault();
        if (tileUrl) {
            let gtmEventDelay = 0;

            if (props.variation === "ad") {
                // log ad click event
                GTM.push({
                    event: "ad_clicked",
                    ad_title: props.title,
                    page_type: `${window.location.pathname}`,
                });

                // set gtm delay to ensure event is fired before navigating away
                gtmEventDelay = 500;
            }

            if (isExternalUrl(tileUrl)) {
                setTimeout(() => {
                    if (
                        (props.link && props.link.target === "_blank") ||
                        props.target === "_blank"
                    ) {
                        window.open(tileUrl);
                    } else {
                        window.location.href = tileUrl;
                    }
                }, gtmEventDelay);
            } else {
                if (
                    (props.link && props.link.target === "_blank") ||
                    props.target === "_blank"
                ) {
                    window.open(tileUrl);
                } else {
                    history.push(tileUrl);
                }
            }
        }
    };

    classes.push(`tile--${props.variation}`);

    if (props.expanded) {
        classes.push(`tile--expanded`);
    }

    if (props.hero) {
        classes.push(`tile--hero`);
    }

    if (props.size) {
        classes.push(`tile--${props.size}`);
    }

    if (props.onSale) {
        classes.push(`tile--sale`);
    }

    if (props.variation === "product") {
        tagIcon = {
            name: "option",
            width: "12.75",
        };
    } else if (props.variation === "club") {
        if (props.tags && props.tags.length) {
            tagIcon = {
                name: "calendar",
                width: "11.69",
            };
        }
    } else if (props.variation === "event" || props.variation === "ticket") {
        if (props.tags && props.tags.length) {
            tagList = props.tags.map((item, i): React.Node => {
                return (
                    <React.Fragment key={i}>
                        <LinkHandler
                            to={item.url}
                            target=""
                            className="tile__tag__host"
                        >
                            {item.tag}
                        </LinkHandler>
                        {props.tags && i < props.tags.length - 1 ? ", " : ""}
                    </React.Fragment>
                );
            });
        }
    }

    const target =
        props.link && props.link.url
            ? props.link.target
            : props.target
            ? props.target
            : "";

    return (
        <div className={classes.join(" ")}>
            {
                // turn to preload
            }
            <RootEl
                to={tileUrl}
                target={target}
                className="tile__top-content"
                aria-label={props.title}
                onClick={handleTileClick}
            >
                {props.image ? (
                    <Image
                        src={
                            props.image
                                ? props.image.sizes &&
                                  props.image.sizes.medium_large
                                    ? props.image.sizes.medium_large
                                    : props.image.url ||
                                      props.image.src ||
                                      props.image
                                : ""
                        }
                        aspectRatio="1:1"
                        alt={props.title}
                        className="tile__image"
                        skeleton={!props.hero ? true : false}
                    />
                ) : null}

                {props.featured && !props.hero ? (
                    <Pill color="pink" className="tile__featured">
                        Featured
                    </Pill>
                ) : null}

                {props.logo ? (
                    <div className="tile__logo">
                        <Image
                            src={props.logo}
                            alt={props.title}
                            aspectRatio="1:1"
                            className="tile__logo__inner"
                        />
                    </div>
                ) : null}
            </RootEl>

            <div className="tile__main-content">
                <div
                    className={`${
                        props.variation === "event" && props.sold_out
                            ? "tile__header--sold-out"
                            : ""
                    } tile__badge-container`}
                >
                    {props.badge ? (
                        <div className={"tile__badge-container__wrapper"}>
                            <Pill
                                color={
                                    GREEN_PILL_LIST.filter(
                                        variation =>
                                            variation === props.variation
                                    ).length > 0
                                        ? "green"
                                        : props.onSale
                                        ? "red"
                                        : "dark"
                                }
                                className="tile__badge"
                            >
                                {props.badge}
                            </Pill>
                            {props.isPastEvent && (
                                <Pill color="grey" className="tile__past">
                                    PAST EVENT
                                </Pill>
                            )}
                            {props.variation === "event" && props.expanded ? (
                                <EventStateBadge
                                    sold_out={props.sold_out}
                                    cancelled={props.cancelled}
                                />
                            ) : null}
                            {props.eyebrow &&
                                !props.hero &&
                                !props.cancelled &&
                                props.expanded && (
                                    <div className="tile__eyebrow">
                                        {props.eyebrow}
                                    </div>
                                )}
                            {status && props.expanded && (
                                // TODO: Should use Pill component
                                <span
                                    className={`tile__club_event__status ${
                                        status.class
                                    } pill ${
                                        status.class === "publish"
                                            ? "pill--lime-green"
                                            : "pill--grey-contrast"
                                    }`}
                                >
                                    <Circle className="tile__club_event__circle" />
                                    {status.label}
                                </span>
                            )}
                        </div>
                    ) : null}

                    {status && !props.expanded ? (
                        <span
                            className={`tile__club_event__status ${status.class}`}
                        >
                            <Circle className="tile__club_event__circle" />
                            {status.label}
                        </span>
                    ) : null}
                </div>
                {props.eyebrow &&
                !props.hero &&
                !props.cancelled &&
                !props.expanded ? (
                    <div className="tile__eyebrow">{props.eyebrow}</div>
                ) : null}
                {props.variation === "event" && !props.expanded ? (
                    <EventStateBadge
                        sold_out={props.sold_out}
                        cancelled={props.cancelled}
                    />
                ) : null}
                <RootEl
                    className="tile__main-content__text"
                    to={tileUrl}
                    target={props.target}
                    onClick={handleTileClick}
                >
                    <h3 className="tile__title">{props.title}</h3>

                    {!props.hero ? (
                        <>
                            {props.variation === "club_membership" ? (
                                <div className="tile__desc--membership">
                                    <div>
                                        <span>
                                            <strong>Joined </strong>
                                        </span>
                                        <span>{props.joined_date}</span>
                                    </div>
                                    <div>
                                        <span>
                                            <strong>Expiry </strong>
                                        </span>
                                        <span>{props.expiry_date}</span>
                                    </div>
                                </div>
                            ) : props.description && !props.noDescription ? (
                                <p
                                    className="tile__desc"
                                    dangerouslySetInnerHTML={{
                                        __html: props.description,
                                    }}
                                />
                            ) : null}
                        </>
                    ) : null}
                </RootEl>
                {props.variation === "club_membership" ? (
                    <TextButton
                        customButton
                        className="tile__cta"
                        to={tileUrl}
                        target=""
                        onClick={handleTileClick}
                    >
                        Club details
                    </TextButton>
                ) : null}

                {props.tags ? (
                    <div className="tile__tag">
                        {tagIcon ? (
                            <span
                                className={`tile__tag__icon tile__tag__icon--${tagIcon.name}`}
                            >
                                <Icon {...tagIcon} />
                            </span>
                        ) : null}
                        <div className="tile__tag__text">
                            {tagList ? (
                                <React.Fragment>
                                    <span className="tile__tag__label">
                                        Hosted by{" "}
                                    </span>
                                    {tagList}
                                </React.Fragment>
                            ) : (
                                props.tags
                            )}
                        </div>
                    </div>
                ) : null}

                {props.alert ? (
                    <div className="tile__tag tile__tag--alert">
                        <span className={`tile__tag__icon`}>
                            <Icon name="exclamation" />
                        </span>
                        <div className="tile__tag__text">{props.alert}</div>
                    </div>
                ) : null}

                {props.variation === "ticket" && (
                    <div className="tile__ticket__details">
                        {!props.isPastEvent && (
                            <Button
                                type="button"
                                styles={["green-secondary"]}
                                onClick={props.onClick}
                            >
                                <Icon name="qr" width="24" />
                                <span>
                                    View
                                    {` ${maybePluralize(
                                        props.ticket_count,
                                        "ticket"
                                    )}`}
                                </span>
                            </Button>
                        )}
                        <TextButton
                            customButton
                            className="tile__ticket__details__cta"
                            to={tileUrl}
                            target=""
                            onClick={handleTileClick}
                        >
                            Event details
                        </TextButton>
                    </div>
                )}

                {props.variation === "club_event" ? (
                    <div className="tile__club_event__footer">
                        <Button
                            type="button"
                            styles={["green-secondary"]}
                            to={props.url}
                        >
                            <span>Manage event</span>
                        </Button>
                        {props.manualAttendanceAllowed ? (
                            <Button
                                type="button"
                                styles={["green-secondary"]}
                                iconBefore="edit"
                                to={props.url}
                            >
                                <span>Submit attendees</span>
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default Tile;
